import React, { useEffect, useState } from 'react';
import { createId } from '@/lib/ids';

const ALERT_MODAL_EVENT = 'k2:alert-modal';

type AlertModalRequest = {
  id: string;
  message: string;
  title?: string;
  confirmText?: string;
  cancelText?: string;
  mode?: 'alert' | 'confirm';
  isDestructive?: boolean;
  onConfirm?: () => void;
  onCancel?: () => void;
};

type AlertModalOptions = {
  title?: string;
  confirmText?: string;
};

type ConfirmModalOptions = {
  title?: string;
  confirmText?: string;
  cancelText?: string;
  isDestructive?: boolean;
};

export const showAlertModal = (message: string, options: AlertModalOptions = {}) => {
  if (typeof window === 'undefined') return;

  const request: AlertModalRequest = {
    id: createId('alert'),
    message,
    title: options.title,
    confirmText: options.confirmText
  };

  window.dispatchEvent(new CustomEvent<AlertModalRequest>(ALERT_MODAL_EVENT, { detail: request }));
};

export const showConfirmModal = (
  message: string,
  options: ConfirmModalOptions = {}
): Promise<boolean> => {
  if (typeof window === 'undefined') return Promise.resolve(false);

  return new Promise((resolve) => {
    const request: AlertModalRequest = {
      id: createId('alert'),
      message,
      title: options.title,
      confirmText: options.confirmText || 'Confirm',
      cancelText: options.cancelText || 'Cancel',
      mode: 'confirm',
      isDestructive: options.isDestructive !== false,
      onConfirm: () => resolve(true),
      onCancel: () => resolve(false)
    };

    window.dispatchEvent(
      new CustomEvent<AlertModalRequest>(ALERT_MODAL_EVENT, { detail: request })
    );
  });
};

export const AlertModalHost: React.FC = () => {
  const [queue, setQueue] = useState<AlertModalRequest[]>([]);

  useEffect(() => {
    const handleAlert = (event: Event) => {
      const customEvent = event as CustomEvent<AlertModalRequest>;
      if (!customEvent.detail?.message) return;
      setQueue((previous) => [...previous, customEvent.detail]);
    };

    window.addEventListener(ALERT_MODAL_EVENT, handleAlert as EventListener);
    return () => window.removeEventListener(ALERT_MODAL_EVENT, handleAlert as EventListener);
  }, []);

  const activeAlert = queue[0];
  if (!activeAlert) return null;

  const closeActiveAlert = () => setQueue((previous) => previous.slice(1));
  const handleConfirm = () => {
    activeAlert.onConfirm?.();
    closeActiveAlert();
  };
  const handleCancel = () => {
    activeAlert.onCancel?.();
    closeActiveAlert();
  };

  return (
    <div className="fixed inset-0 z-[120] bg-black/60 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-2xl p-6 sm:p-8 max-w-md w-full text-center text-slate-900">
        <h3 className="text-xl font-black mb-2">{activeAlert.title || 'Notice'}</h3>
        <p className="text-slate-600 text-sm mb-6 whitespace-pre-line">{activeAlert.message}</p>
        {activeAlert.mode === 'confirm' ? (
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={handleCancel}
              className="w-full border border-slate-300 text-slate-700 py-3 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-slate-100 transition-colors"
            >
              {activeAlert.cancelText || 'Cancel'}
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              className={`w-full py-3 rounded-xl font-black uppercase text-xs tracking-widest transition-colors ${
                activeAlert.isDestructive
                  ? 'bg-red-600 text-white hover:bg-red-700'
                  : 'bg-slate-900 text-white hover:bg-slate-800'
              }`}
            >
              {activeAlert.confirmText || 'Confirm'}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={closeActiveAlert}
            className="w-full bg-slate-900 text-white py-3 rounded-xl font-black uppercase text-xs tracking-widest hover:bg-slate-800 transition-colors"
          >
            {activeAlert.confirmText || 'OK'}
          </button>
        )}
      </div>
    </div>
  );
};
